import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Languages } from 'lucide-react';
import { TRANSLATE_COPY, translateLanguageLabel } from '@saesak/shared';
import { Button } from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { getAppSettings } from '@/lib/app-settings';

/**
 * 번역 보기 (§5.28, v1.18) — 알림장·공지 본문 아래에 붙는 블록.
 * 앱 설정에서 번역을 켠 이용자에게만 보인다. 번역은 저장하지 않는다.
 */
export function TranslateBlock({ text }: { text: string }) {
  const settings = getAppSettings();
  const [translated, setTranslated] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ['ai', 'translate', 'status'],
    queryFn: () => apiData<{ available: boolean }>('/ai/translate/status'),
    staleTime: 5 * 60_000,
    enabled: settings.translateEnabled,
  });

  const run = useMutation({
    mutationFn: () =>
      apiData<{ translated: string }>('/ai/translate', {
        method: 'POST',
        body: { text, targetLang: settings.translateLang },
      }),
    onSuccess: (result) => {
      setTranslated(result.translated);
      setOpen(true);
      setError(null);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : TRANSLATE_COPY.unavailable),
  });

  if (!settings.translateEnabled || !status.data?.available || !text.trim()) return null;

  return (
    <div className="mt-3">
      <Button
        size="sm"
        variant="outline"
        icon={<Languages size={14} />}
        loading={run.isPending}
        onClick={() => {
          if (translated) setOpen((prev) => !prev);
          else run.mutate();
        }}
      >
        {open ? TRANSLATE_COPY.hideButton : TRANSLATE_COPY.showButton}
        {` · ${translateLanguageLabel(settings.translateLang).split(' ')[0]}`}
      </Button>

      {error && (
        <p role="alert" className="mt-2 rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
          {error}
        </p>
      )}

      {open && translated && (
        <div className="mt-2 rounded-sm bg-info-soft px-3.5 py-3">
          <p className="whitespace-pre-wrap text-base leading-relaxed">{translated}</p>
          <p className="mt-2 text-sm text-ink-sub">{TRANSLATE_COPY.caution}</p>
        </div>
      )}
    </div>
  );
}
